import React from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  useTheme,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const TermsOfServicePage: React.FC = () => {
  const theme = useTheme();

  const sectionHeaderSx = {
    color: theme.palette.text.primary,
    mt: 5,
    mb: 2,
    fontWeight: 600,
  };

  const bodySx = {
    color: theme.palette.text.secondary,
    mb: 2,
    lineHeight: 1.8,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit" underline="hover">
            Home
          </Link>
          <Typography color="text.primary">Terms of Service</Typography>
        </Breadcrumbs>

        <Typography variant="h3" sx={{ color: theme.palette.text.primary, mb: 1, fontWeight: 700 }}>
          Terms of Service
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
          Conditions Générales d&apos;Utilisation
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
          Last updated: March 2026
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* 1. Definitions */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          1. Definitions
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="&ldquo;AmudFin&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;"
              secondary="AmudFin S.A., a société anonyme incorporated under the laws of the Grand Duchy of Luxembourg, with its registered office at 9, Boulevard Prince Henri, L-1724 Luxembourg."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="&ldquo;Client&rdquo;, &ldquo;you&rdquo;, &ldquo;your&rdquo;"
              secondary="The legal entity that has entered into a subscription agreement with AmudFin for access to the Platform."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="&ldquo;Platform&rdquo;"
              secondary="The AmudFin web-based software application, including all APIs, interfaces, and related services provided under these Terms."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="&ldquo;User&rdquo;"
              secondary="Any individual authorised by the Client to access and use the Platform on the Client's behalf."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="&ldquo;Client Data&rdquo;"
              secondary="All data, including personal data, fund data, transaction records, and documents, that the Client or its Users upload, submit, or generate through the Platform."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="&ldquo;Subscription Period&rdquo;"
              secondary="The period during which the Client has a valid, active subscription to access the Platform, as specified in the applicable order form."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 2. Scope of Service */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          2. Scope of Service
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin provides a cloud-based operational infrastructure platform for Luxembourg fund
          management. The Platform enables fund administrators, management companies, and compliance
          officers to manage fund operations, including NAV calculations, investor registers,
          transaction processing, regulatory reporting, and compliance monitoring.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>The Platform is a software tool and does not constitute financial advice,
          investment advice, tax advice, or legal advice.</strong> AmudFin is not a regulated
          financial institution, investment fund, management company, or investment adviser. The
          Platform does not make investment decisions, manage client assets, or provide
          personalised recommendations. The Client retains full responsibility for all fund
          management decisions and regulatory compliance obligations.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AI-assisted compliance scoring and predictive analytics provided by the Platform are
          decision-support tools only. They do not replace professional judgement, legal counsel,
          or regulatory expertise. The Client must independently verify all compliance assessments
          and regulatory filings.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 3. Account Registration */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          3. Account Registration and Security
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          To access the Platform, the Client must register an account and designate at least one
          administrative User. The Client is responsible for:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Providing accurate, complete, and current registration information" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Maintaining the confidentiality of all User credentials, including passwords and two-factor authentication devices" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Ensuring that only authorised individuals access the Platform under the Client's account" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Promptly notifying AmudFin of any unauthorised access or security breach at security@amudfin.com" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Configuring appropriate role-based access controls and approval workflows within the Platform" />
          </ListItem>
        </List>
        <Typography variant="body1" sx={bodySx}>
          AmudFin reserves the right to suspend access to any User account that we reasonably
          believe has been compromised, is being used in violation of these Terms, or poses a
          security risk to the Platform or other Clients.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 4. Acceptable Use */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          4. Acceptable Use Policy
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The Client agrees to use the Platform only for its intended purpose of fund management
          operations. The following uses are prohibited:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Using the Platform to facilitate money laundering, terrorist financing, or any other financial crime" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Attempting to gain unauthorised access to other Clients' data, Platform infrastructure, or internal systems" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Reverse engineering, decompiling, or disassembling any part of the Platform" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Using automated scripts, bots, or scraping tools to access the Platform outside of documented APIs" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Uploading malicious code, viruses, or any content designed to disrupt Platform operations" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Sublicensing, reselling, or providing access to the Platform to third parties without prior written consent" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Using the Platform in violation of any applicable law, regulation, or industry standard" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 5. Intellectual Property */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          5. Intellectual Property
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          All intellectual property rights in the Platform, including its source code, architecture,
          design, user interface, documentation, algorithms, and AI models, are and remain the
          exclusive property of AmudFin S.A. The Client receives a limited, non-exclusive,
          non-transferable, revocable licence to use the Platform during the Subscription Period
          solely for the Client&apos;s internal business purposes.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Client Data remains the exclusive property of the Client. AmudFin does not claim any
          ownership rights over Client Data. We process Client Data solely to provide the Platform
          services and as described in our Privacy Policy and Data Processing Agreement.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin may use anonymised, aggregated data derived from Platform usage to improve its
          services, provided that such data cannot be used to identify any individual Client or
          its Users.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 6. Data Processing */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          6. Data Processing
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The processing of personal data in connection with the Platform is governed by our{' '}
          <Link component={RouterLink} to="/privacy-policy" color="primary">
            Privacy Policy
          </Link>{' '}
          and the Data Processing Agreement between AmudFin (as processor) and the Client (as
          controller). Where the Client processes personal data of its own clients or investors
          through the Platform, the Client remains the data controller and is responsible for
          ensuring a valid legal basis for such processing under the GDPR.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 7. Service Availability */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          7. Service Availability
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin endeavours to maintain high availability of the Platform and will use commercially
          reasonable efforts to minimise downtime. However, the Platform is provided on a best-effort
          basis, and AmudFin does not guarantee uninterrupted, error-free, or continuous access.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Planned maintenance windows will be communicated at least 48 hours in advance via the
          Platform and email. Emergency maintenance necessary to address security vulnerabilities
          or critical issues may be performed without prior notice. AmudFin shall not be liable for
          any unavailability caused by factors outside our reasonable control, including force
          majeure events, third-party service failures, or Client-side network issues.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 8. Fees and Payment */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          8. Fees and Payment
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The Client shall pay the subscription fees specified in the applicable order form.
          Fees are invoiced in advance on a monthly or annual basis as agreed. All fees are quoted
          exclusive of VAT, which shall be added at the applicable Luxembourg rate (currently 17%).
          Late payments are subject to interest at the legal rate applicable in Luxembourg under
          Article 15 of the Law of 18 April 2004 on commercial payment delays.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin reserves the right to suspend access to the Platform if invoiced fees remain
          unpaid for more than 30 days after the due date, following a written reminder.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 9. Limitation of Liability */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          9. Limitation of Liability
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          To the maximum extent permitted by Luxembourg law:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="AmudFin's total aggregate liability under these Terms shall not exceed the total fees paid by the Client in the twelve (12) months preceding the event giving rise to the claim." />
          </ListItem>
          <ListItem>
            <ListItemText primary="AmudFin shall not be liable for any indirect, incidental, consequential, special, or punitive damages, including loss of profits, loss of data, loss of business opportunity, or regulatory fines, whether arising in contract, tort, or otherwise." />
          </ListItem>
          <ListItem>
            <ListItemText primary="AmudFin shall not be liable for any decisions made by the Client based on data, reports, scores, or analytics generated by the Platform. The Client is solely responsible for verifying the accuracy and appropriateness of all outputs before acting on them." />
          </ListItem>
        </List>
        <Typography variant="body1" sx={bodySx}>
          Nothing in these Terms excludes or limits liability for fraud, wilful misconduct, or any
          liability that cannot be excluded under applicable Luxembourg law.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 10. Indemnification */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          10. Indemnification
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The Client shall indemnify, defend, and hold harmless AmudFin, its directors, officers,
          employees, and agents from and against any claims, damages, losses, liabilities, costs,
          and expenses (including reasonable legal fees) arising out of or relating to:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="The Client's breach of these Terms or any applicable law or regulation" />
          </ListItem>
          <ListItem>
            <ListItemText primary="The Client's use of the Platform in a manner not authorised by these Terms" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Any claim by a third party arising from Client Data or the Client's processing of personal data through the Platform" />
          </ListItem>
          <ListItem>
            <ListItemText primary="The Client's failure to maintain adequate security controls for its User accounts" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 11. Confidentiality */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          11. Confidentiality
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Each party agrees to maintain the confidentiality of the other party&apos;s confidential
          information and not to disclose it to third parties without prior written consent, except
          as required by law, regulation, or court order. Confidential information includes, but is
          not limited to, Client Data, Platform architecture, pricing terms, and business strategies.
          This obligation survives termination of these Terms for a period of five (5) years.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 12. Termination */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          12. Termination
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Either party may terminate these Terms:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="By providing 90 days' written notice before the end of the current Subscription Period" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Immediately, if the other party commits a material breach that remains uncured for 30 days after written notice" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Immediately, if the other party becomes insolvent, enters administration, or ceases to carry on business" />
          </ListItem>
        </List>
        <Typography variant="body1" sx={bodySx}>
          Upon termination, AmudFin will provide the Client with a copy of all Client Data in a
          structured, machine-readable format (JSON or CSV) within 30 days of the termination date.
          After this 30-day period, Client Data will be securely deleted in accordance with our
          data retention policy, subject to any overriding legal retention obligations.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 13. Governing Law */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          13. Governing Law and Jurisdiction
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          These Terms shall be governed by and construed in accordance with the laws of the Grand
          Duchy of Luxembourg, without regard to its conflict of law provisions. Any dispute
          arising out of or in connection with these Terms shall be submitted to the exclusive
          jurisdiction of the courts of Luxembourg City (Tribunal d&apos;arrondissement de
          Luxembourg).
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 14. Modifications */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          14. Modification of Terms
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin reserves the right to modify these Terms at any time. Material changes will be
          communicated to the Client via email and in-platform notification at least 30 days before
          they take effect. Continued use of the Platform after the effective date of modified Terms
          constitutes acceptance of those changes. If the Client does not agree to the modified
          Terms, the Client may terminate its subscription in accordance with Section 12.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 15. General Provisions */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          15. General Provisions
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>Severability:</strong> If any provision of these Terms is held to be invalid or
          unenforceable, the remaining provisions shall continue in full force and effect.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>Waiver:</strong> No failure or delay by either party in exercising any right under
          these Terms shall constitute a waiver of that right.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>Entire Agreement:</strong> These Terms, together with the applicable order form,
          Privacy Policy, and Data Processing Agreement, constitute the entire agreement between
          the parties and supersede all prior agreements and understandings.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>Assignment:</strong> The Client may not assign or transfer these Terms without
          AmudFin&apos;s prior written consent. AmudFin may assign these Terms in connection with a
          merger, acquisition, or sale of all or substantially all of its assets.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>Language:</strong> These Terms are drafted in English. In the event of a conflict
          between the English version and any translation, the English version shall prevail.
        </Typography>

        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            For questions about these Terms of Service, contact us at legal@amudfin.com.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsOfServicePage;
